import React from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';

/**
 * PageHeader — consistent enterprise-style page header.
 *
 * Props:
 *   title       {string}   required — page title
 *   subtitle    {string}   optional — short description line
 *   gradient    {string}   optional — CSS gradient for the accent bar (default: purple)
 *   actions     {node}     optional — right-side action buttons / chips
 *   meta        {node}     optional — extra content below title (e.g. count badge, chips)
 */
export default function PageHeader({
  title,
  subtitle,
  gradient = 'linear-gradient(90deg, #7C3AED, #A78BFA)',
  actions,
  meta,
  sx = {},
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        mb: 3,
        borderRadius: 2.5,
        overflow: 'hidden',
        boxShadow: 'none',
        ...sx,
      }}
    >
      <Box sx={{ height: 3, background: gradient, flexShrink: 0 }} />
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box minWidth={0}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <Typography variant="h6" fontWeight={700} noWrap>
                {title}
              </Typography>
              {meta}
            </Stack>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.25, lineHeight: 1.5 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {actions && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexShrink={0}
              flexWrap="wrap"
            >
              {actions}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
